import { apiService, ApiResponse, ChatSession, ChatMessage, FileUploadResponse } from './apiService';
import { API_CONFIG } from './apiConfig';
import { ChatLetterOutput, ChatLetterSummary } from '../types/chat';

// Chat-specific interfaces
export interface ChatOptions {
  title?: string;
  metadata?: Record<string, any>;
  tag?:string;
  initialMessage?: string;
  claimId?: string;
  experiments?: Record<string, boolean>;
  initialAssistantMessage?: string;
}

export interface MessageOptions {
  attachments?: File[];
  metadata?: Record<string, any>;
  priority?: 'low' | 'normal' | 'high';
}

export interface ChatSearchOptions {
  query?: string;
  limit?: number;
  offset?: number;
  page?: number;  // For DRF PageNumberPagination
  sortBy?: 'createdAt' | 'updatedAt' | 'title';
  sortOrder?: 'asc' | 'desc';
  // Receive non appeal type chants only
  non_appeal_only?: boolean;
}

// Chat Service Class
class ChatService {
  private apiService = apiService;

  // Create a new chat session
  async createChat(options: ChatOptions = {}): Promise<ApiResponse<ChatSession>> {
    try {
      const response = await this.apiService.createChat(options.title, options.tag, options.claimId, options.experiments, options.initialAssistantMessage);
      // Send initial message if provided
      if (response.success && options.initialMessage) {
        
        await this.sendMessage(response.data!.id, options.initialMessage, {
          metadata: options.metadata,
        });
      }
      
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create chat',
      };
    }
  }

  // Get all chat sessions with search and pagination
  // Backend uses DRF PageNumberPagination, so we use 'page' parameter
  async getChats(options: ChatSearchOptions = {}): Promise<ApiResponse<ChatSession[]>> {
    try {
      const queryParams = new URLSearchParams();
      
      if (options.query) queryParams.append('q', options.query);
      // Backend uses PageNumberPagination, so prefer 'page' over 'limit'/'offset'
      if (options.page !== undefined) {
        queryParams.append('page', options.page.toString());
      } else if (options.limit && options.offset !== undefined) {
        // Fallback: calculate page from limit/offset
        const page = Math.floor(options.offset / options.limit) + 1;
        queryParams.append('page', page.toString());
      }

      if (options.sortBy) queryParams.append('sortBy', options.sortBy);
      if (options.sortOrder) queryParams.append('sortOrder', options.sortOrder);
      if (typeof options.non_appeal_only !== 'undefined') queryParams.append('non_appeal_only', 'true');

      const endpoint = `/chats/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      console.log('[ChatService] getChats calling endpoint:', endpoint);
      // Backend returns paginated format: { results: [...], count, next, previous }
      const response = await this.apiService.request<{ results: ChatSession[]; count?: number; next?: string | null; previous?: string | null }>(endpoint);
      console.log('[ChatService] getChats response:', response);
      
      // Extract results array from paginated response
      if (response.success && response.data) {
        if (Array.isArray(response.data)) {
          // If backend returns array directly (shouldn't happen with pagination, but handle it)
          return { ...response, data: response.data };
        } else if (response.data && typeof response.data === 'object' && 'results' in response.data) {
          // Paginated response: extract results array
          return { 
            ...response, 
            data: (response.data as any).results 
          };
        }
      }
      
      return {
        success: false,
        error: 'Invalid response format from server',
        status: response.status
      };
    } catch (error) {
      console.error('[ChatService] getChats error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get chats',
      };
    }
  }

  // Get a specific chat session
  async getChat(chatId: string): Promise<ApiResponse<ChatSession>> {
    try {
      return await this.apiService.getChat(chatId);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get chat',
      };
    }
  }

  // Send a message to a chat
  async sendMessage(
    chatId: string,
    content: string,
    options: MessageOptions = {}
  ): Promise<ApiResponse<ChatMessage>> {
    try {
      // Process attachments if any
      let processedAttachments: File[] = [];
      
      if (options.attachments && options.attachments.length > 0) {
        processedAttachments = await this.processAttachments(options.attachments);
      }

      const response = await this.apiService.sendMessage(
        chatId,
        content,
        processedAttachments
      );

      // Add metadata to the message if provided
      if (response.success && options.metadata) {
        // Note: This would require the API to support metadata updates
        // For now, we'll just return the response as is
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send message',
      };
    }
  }

  // Process and validate attachments
  private async processAttachments(files: File[]): Promise<File[]> {
    const processedFiles: File[] = [];
    
    for (const file of files) {
      try {
        // Validate file type and size
        if (this.isValidFile(file)) {
          // Process file if needed (e.g., compress images, validate PDFs)
          const processedFile = await this.processFile(file);
          processedFiles.push(processedFile);
        }
      } catch (error) {
      }
    }
    
    return processedFiles;
  }

  // Validate file
  private isValidFile(file: File): boolean {
    const allowedTypes = API_CONFIG.UPLOAD_LIMITS.ALLOWED_TYPES;

    if (!allowedTypes.includes(file.type as any)) {
      throw new Error(`File type ${file.type} is not supported.`);
    }

    return true;
  }

  // Process individual file
  private async processFile(file: File): Promise<File> {
    // For now, return the file as-is
    // In the future, this could include:
    // - Image compression
    // - PDF validation
    // - Text extraction
    // - Virus scanning
    return file;
  }

  // Delete a chat session
  async deleteChat(chatId: string): Promise<ApiResponse<void>> {
    try {
      return await this.apiService.deleteChat(chatId);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete chat',
      };
    }
  }

  // Archive a chat (soft delete)
  async archiveChat(chatId: string): Promise<ApiResponse<void>> {
    try {
      return await this.apiService.request<void>(`/chats/${chatId}/archive/`, {
        method: 'PUT',
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to archive chat',
      };
    }
  }

  // Close a chat (terminal, irreversible). Backend is idempotent.
  async closeChat(chatId: string): Promise<ApiResponse<ChatSession>> {
    try {
      return await this.apiService.closeChat(chatId);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to close chat',
      };
    }
  }

  // Clone a chat completely. Returns a new, independent, open chat that
  // inherits the source's history, files, claim and context.
  async cloneChat(chatId: string): Promise<ApiResponse<ChatSession>> {
    try {
      return await this.apiService.cloneChat(chatId);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to clone chat',
      };
    }
  }

  // Pin/unpin a chat
  async toggleChatPin(chatId: string, isPinned: boolean): Promise<ApiResponse<ChatSession>> {
    try {
      const url = isPinned ? `/chats/${chatId}/pin/` : `/chats/${chatId}/unpin/`;

      return await this.apiService.request<ChatSession>(url, {
        method: 'PUT',
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to toggle chat pin',
      };
    }
  }

  // Pin/unpin a file attachment. Backend contract: PATCH /v1/files/<file_id>/
  // with { is_pinned }, returning the persisted { is_pinned, display_name }.
  async toggleFilePin(
    chatId: string,
    fileId: string,
    isPinned: boolean
  ): Promise<ApiResponse<{ is_pinned: boolean; display_name: string }>> {
    try {
      return await this.apiService.request<{ is_pinned: boolean; display_name: string }>(
        `/files/${fileId}/`,
        {
          method: 'PATCH',
          body: JSON.stringify({ is_pinned: isPinned }),
        }
      );
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to toggle file pin',
      };
    }
  }

  // List the chat's generated documents (appeal letters), without their bodies.
  // Accepts either a bare array or a DRF paginated { results } body.
  async getChatOutputDocuments(chatId: string): Promise<ApiResponse<ChatLetterSummary[]>> {
    try {
      const response = await this.apiService.request<
        ChatLetterSummary[] | { results?: ChatLetterSummary[] }
      >(`/chats/${chatId}/output-documents/`);
      if (!response.success) {
        return { success: false, error: response.error, status: response.status };
      }
      const data = response.data;
      return {
        success: true,
        data: Array.isArray(data) ? data : data?.results ?? [],
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load chat letters',
      };
    }
  }

  // Fetch the chat's most recently generated document (appeal letter).
  async getLatestChatOutputDocument(chatId: string): Promise<ApiResponse<ChatLetterOutput>> {
    try {
      return await this.apiService.request<ChatLetterOutput>(
        `/chats/${chatId}/output-documents/latest/`,
      );
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load latest chat letter',
      };
    }
  }

  // Fetch one version of a generated document, with its body.
  async getChatOutputDocument(
    chatId: string,
    generation: number,
  ): Promise<ApiResponse<ChatLetterOutput>> {
    try {
      return await this.apiService.request<ChatLetterOutput>(
        `/chats/${chatId}/output-documents/${generation}/`,
      );
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load chat letter',
      };
    }
  }

  // Update a generated document (appeal letter), addressed by its generation.
  // Pin/rename work on any version; `content` edits only the chat's latest and
  // comes back 409 `document_output_stale` otherwise (data.latest_version).
  async updateChatOutputDocument(
    chatId: string,
    generation: number,
    changes: Partial<Pick<ChatLetterOutput, 'is_pinned' | 'display_name' | 'content'>>,
  ): Promise<ApiResponse<ChatLetterOutput>> {
    try {
      return await this.apiService.request<ChatLetterOutput>(
        `/chats/${chatId}/output-documents/${generation}/`,
        {
          method: 'PATCH',
          body: JSON.stringify(changes),
        },
      );
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update chat letter',
      };
    }
  }

  // Rename a file attachment
  async renameFile(chatId: string, fileId: string, name: string): Promise<ApiResponse<void>> {
    try {
      return await this.apiService.request<void>(`/files/${fileId}/`, {
        method: 'PATCH',
        body: JSON.stringify({ display_name: name }),
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to rename file',
      };
    }
  }

  // Update chat title
  async updateChatTitle(chatId: string, title: string, isPinned?: boolean): Promise<ApiResponse<ChatSession>> {
    try {
      return await this.apiService.updateChatTitle(chatId, title, isPinned);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update chat title',
      };
    }
  }

  // Upload file (browser streams body; do not read file into memory)
  async uploadFile(file: File): Promise<ApiResponse<FileUploadResponse>> {
    try {
      return await this.apiService.uploadFile(file);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to upload file',
      };
    }
  }

  // Get chat statistics
  async getChatStats(chatId: string): Promise<ApiResponse<{
    messageCount: number;
    wordCount: number;
    attachmentCount: number;
    lastActivity: string;
  }>> {
    try {
      return await this.apiService.request(`/chats/${chatId}/stats/`);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get chat stats',
      };
    }
  }

  // Export chat to various formats
  async exportChat(
    chatId: string,
    format: 'pdf' | 'txt' | 'json' | 'csv'
  ): Promise<ApiResponse<Blob>> {
    try {
      const response = await this.apiService.request(`/chats/${chatId}/export/`, {
        method: 'POST',
        body: JSON.stringify({ format }),
      });

      if (response.success && response.data) {
        // Convert response to blob
        const blob = new Blob([JSON.stringify(response.data)], {
          type: this.getMimeType(format),
        });
        return { success: true, data: blob };
      }

      return response as any;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to export chat',
      };
    }
  }

  // Get MIME type for export format
  private getMimeType(format: string): string {
    switch (format) {
      case 'pdf':
        return 'application/pdf';
      case 'txt':
        return 'text/plain';
      case 'json':
        return 'application/json';
      case 'csv':
        return 'text/csv';
      default:
        return 'application/octet-stream';
    }
  }

  // Search within chat messages
  async searchChatMessages(
    chatId: string,
    query: string,
    options: {
      limit?: number;
      offset?: number;
      includeAttachments?: boolean;
    } = {}
  ): Promise<ApiResponse<ChatMessage[]>> {
    try {
      const queryParams = new URLSearchParams({ q: query });
      
      if (options.limit) queryParams.append('limit', options.limit.toString());
      if (options.offset) queryParams.append('offset', options.offset.toString());
      if (options.includeAttachments) queryParams.append('includeAttachments', 'true');
      
      const endpoint = `/chats/${chatId}/search/?${queryParams.toString()}`;
      return await this.apiService.request<ChatMessage[]>(endpoint);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to search chat messages',
      };
    }
  }

  // Get chat suggestions based on content
  async getChatSuggestions(
    chatId: string,
    context?: string
  ): Promise<ApiResponse<{
    suggestions: string[];
    relatedTopics: string[];
    nextSteps: string[];
  }>> {
    try {
      const body = context ? { context } : {};
      
      return await this.apiService.request(`/chats/${chatId}/suggestions/`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get chat suggestions',
      };
    }
  }
}

// Create and export a singleton instance
export const chatService = new ChatService();

// Export the class for custom instances
export default ChatService;
